// © 2026 Holmes Computer Consultants. All rights reserved. -- www.holmesconsultants.com
//
// Who may be heard, and by whom (M8-09).
//
// 06 §4: voice "auto-starts on entry … with a mute control in the wrist menu and in the HUD", and
// 06 §5 lists "mute voice chat" in the Sound section beside the master mute. This owns the two
// booleans behind those controls and nothing else — no nodes, no tracks, no DOM. `voiceLink.ts`
// acts on one of them and `voicePlayback.ts` acts on the other, and neither of them decides.
//
// **Two mutes, not one, and this is the whole design.** "I do not want to be heard" and "I do not
// want to hear" are different needs that a player has at different moments, and a single control
// does the wrong half of it whichever way it is wired. Somebody taking a phone call needs their
// microphone off and everybody else still audible; somebody whose room has one unpleasant person
// in it needs the opposite. A product that offers one toggle forces the second player to leave.
//
// **The microphone mute is the one that matters and it is listed first everywhere.** It is the
// only control in this product whose failure is a privacy failure rather than an annoyance, which
// is why it is the item the wrist menu draws directly rather than behind a submenu, and why its
// label states the current condition rather than the action.
//
// **Both are remembered across sessions, and that is a deliberate asymmetry with everything else
// in the arcade.** A player who muted their microphone made a decision about their microphone; a
// page refresh, a headset going to sleep, or the four-second interruption window in
// `arcadePresence.ts` is not that player changing their mind. Coming back live because the process
// restarted is the exact failure mode that makes people distrust a mute button, so the state
// outlives the process. The default is unmuted in both directions, because 06 §4 says voice
// auto-starts and a room where nobody can hear anybody is not the room that was asked for.
//
// **Muted is not disconnected.** Nothing here hangs up a call, releases a microphone or tears
// down a graph. Mute has to be instant and reversible in both directions, and every one of those
// would make un-muting an asynchronous renegotiation — on a headset, a permission prompt, which
// is a system dialogue that ends the immersive session. See the notes in `voiceLink.ts`.
//
// **A refused microphone lives here too, and it is not a mute** (M8-10). It is the same sentence
// to a player — "nobody can hear me" — reached by a route they did not choose, and the controls
// have to tell those two apart or they lie. A mute control reading "Microphone on" over a
// microphone the browser refused is precisely the failure this whole file is written against, and
// it is the one M8-09 would have shipped on its own. So `access` sits beside the two choices,
// **is not persisted** — it is a fact about this moment, and a stored "refused" would outlive a
// player fixing their browser settings — and it is what makes the difference between "off,
// because you turned it off" and "off, because your browser will not allow it".

import { readLocal, writeLocal, type StorageLike } from '../storage/local'

/** Where the two answers live. Namespaced like every other key this product writes. */
export const MIC_MUTE_KEY = 'hcc.pinball.voice.mic-muted'
export const VOICE_MUTE_KEY = 'hcc.pinball.voice.muted'

/**
 * Where "I have read the notice about my microphone" lives (M8-10).
 *
 * Persisted, because the alternative is telling somebody the same thing every time they enter the
 * arcade, and 06 §4 asks for it to be non-blocking rather than merely dismissible — a notice that
 * comes back is a notice in the way. **Cleared the moment the microphone is granted**, so a player
 * who fixes their browser settings and is later refused again is told again rather than left
 * silently mute.
 */
export const MIC_NOTICE_KEY = 'hcc.pinball.voice.mic-blocked-seen'

/**
 * Whether this browser will let the page use a microphone.
 *
 * `'unknown'` until it has been asked, which is most of a session — voice only starts when a
 * shared arcade is joined, and the controls have to draw something sensible before then. Drawn as
 * "on", because that is what it will be: 06 §4 auto-starts voice on entry, and a control that
 * pre-emptively reported a problem nobody has had yet would be its own kind of lie.
 */
export type MicrophoneAccess = 'unknown' | 'granted' | 'refused'

/**
 * The one sentence about a refused microphone, said the same way everywhere (M8-10).
 *
 * **It names what still works, and does not apologise.** A player who declined — or whose browser
 * declined for them — still hears everybody in the room, because the call is created with a
 * receive-only transceiver (see `voiceLink.ts`). That is the useful half of the message, and
 * leading with the failure would make a one-way room sound like a broken one. The same wording is
 * used by the HUD chip and by the wrist menu row, so a player who sees both does not have to work
 * out whether they are being told about two different things.
 */
export const MIC_BLOCKED_NOTICE =
  'Your browser is not allowing a microphone — you can still hear everybody in the arcade.'

/** The pair, as a value. Small enough to pass around and compare. */
export interface VoiceMuteState {
  /** This player's microphone is off. Nobody hears them. */
  readonly microphone: boolean
  /** Everybody else's voices are off. They hear nobody. */
  readonly others: boolean
}

export const UNMUTED: VoiceMuteState = { microphone: false, others: false }

/** Everything the three surfaces need in order to draw a voice control honestly. */
export interface VoiceState {
  /** The two choices, which are what a membership applies. */
  readonly mute: VoiceMuteState
  /** What the browser said about a microphone (M8-10). */
  readonly access: MicrophoneAccess
  /** The player has been told about a refused microphone and dismissed it (M8-10). */
  readonly noticed: boolean
}

/**
 * Should a refused microphone be announced right now?
 *
 * A function rather than a field, because it is a question two surfaces ask and neither should be
 * the one that decides. `'unknown'` is not an answer worth interrupting anybody about: it means
 * nobody has asked yet.
 */
export function shouldTellAboutMicrophone(state: VoiceState): boolean {
  return state.access === 'refused' && !state.noticed
}

/**
 * What a control should say.
 *
 * **Stating the condition, not the action.** "Mute" on a button that is already muted is the
 * single most common way to build a mute control that lies: the player reads the label, believes
 * it describes what is happening, and talks. Every label here says what *is*, and the pressed
 * state of the control says what a press would change.
 */
export function micLabel(muted: boolean, access: MicrophoneAccess = 'unknown'): string {
  // **Blocked outranks muted, and that ordering is the point.** Both mean "nobody can hear me",
  // and only one of them is undone by pressing the control. A player whose browser refused,
  // shown "Microphone off", would press it, read "Microphone on", and talk.
  if (access === 'refused') return 'Microphone blocked'
  return muted ? 'Microphone off' : 'Microphone on'
}

export function voiceLabel(muted: boolean): string {
  return muted ? 'Voice chat off' : 'Voice chat on'
}

/**
 * The two booleans, remembered.
 *
 * A tiny class rather than two exported functions because the thing that owns it — the machine
 * runtime — has to hand the same state to a wrist menu, a HUD and a network chunk that is behind
 * a lazy boundary, and three readers of two booleans is exactly the shape that drifts.
 */
export class VoiceMute {
  private micMuted: boolean
  private othersMuted: boolean
  private micAccess: MicrophoneAccess = 'unknown'
  private micNoticed: boolean
  private readonly listeners = new Set<(state: VoiceState) => void>()

  constructor(private readonly storage: StorageLike | undefined) {
    this.micMuted = readBoolean(storage, MIC_MUTE_KEY)
    this.othersMuted = readBoolean(storage, VOICE_MUTE_KEY)
    this.micNoticed = readBoolean(storage, MIC_NOTICE_KEY)
  }

  /** The two choices, which are the part a membership applies. */
  get mute(): VoiceMuteState {
    return { microphone: this.micMuted, others: this.othersMuted }
  }

  get state(): VoiceState {
    return { mute: this.mute, access: this.micAccess, noticed: this.micNoticed }
  }

  get access(): MicrophoneAccess {
    return this.micAccess
  }

  get microphone(): boolean {
    return this.micMuted
  }

  get others(): boolean {
    return this.othersMuted
  }

  setMicrophone(muted: boolean): void {
    if (this.micMuted === muted) return
    this.micMuted = muted
    writeLocal(this.storage, MIC_MUTE_KEY, muted ? '1' : '0')
    this.announce()
  }

  setOthers(muted: boolean): void {
    if (this.othersMuted === muted) return
    this.othersMuted = muted
    writeLocal(this.storage, VOICE_MUTE_KEY, muted ? '1' : '0')
    this.announce()
  }

  toggleMicrophone(): void {
    this.setMicrophone(!this.micMuted)
  }

  toggleOthers(): void {
    this.setOthers(!this.othersMuted)
  }

  /**
   * What the browser said when the microphone was asked for (M8-10).
   *
   * **Granting clears the notice**, so a player who fixes their settings and is refused again
   * later is told again. Without that, "seen once" would mean "never mentioned again on this
   * browser", and the second denial — the one after a Windows update reset a permission — would
   * leave somebody silently mute with nothing on screen saying so.
   */
  setAccess(access: MicrophoneAccess): void {
    if (this.micAccess === access) return
    this.micAccess = access
    if (access === 'granted' && this.micNoticed) {
      this.micNoticed = false
      writeLocal(this.storage, MIC_NOTICE_KEY, '0')
    }
    this.announce()
  }

  /** The player dismissed the notice about their refused microphone (M8-10). */
  dismissMicrophoneNotice(): void {
    if (this.micNoticed) return
    this.micNoticed = true
    writeLocal(this.storage, MIC_NOTICE_KEY, '1')
    this.announce()
  }

  /**
   * Be told when either changes, and **be told the current state at once**.
   *
   * The immediate call is not a convenience. A subscriber that only hears about *changes* starts
   * out of step with a state that was loaded from storage before it existed, which means a player
   * who muted their microphone last session is unmuted for as long as it takes them to notice.
   * Returns its own removal.
   */
  subscribe(listener: (state: VoiceState) => void): () => void {
    this.listeners.add(listener)
    // Guarded like every other delivery. Unguarded, a subscriber that throws on its first call
    // takes down whoever registered it — and the caller registering this is a constructor, so
    // the failure would be a machine that does not build because a panel has a bug in it.
    tell(listener, this.state)
    return () => {
      this.listeners.delete(listener)
    }
  }

  private announce(): void {
    const state = this.state
    // A copy, so a listener that unsubscribes itself while being told does not skip the next one.
    for (const listener of [...this.listeners]) tell(listener, state)
  }
}

/**
 * Hand one subscriber the state, and swallow what it does with it.
 *
 * A subscriber that throws is a bug in that subscriber. It must not stop the *other* subscribers
 * from being told, because one of them is the microphone.
 */
function tell(listener: (state: VoiceState) => void, state: VoiceState): void {
  try {
    listener(state)
  } catch {
    // Deliberately silent. There is nothing to tell a player about a panel that failed to redraw,
    // and the state itself is already correct.
  }
}

/** A stored flag, read defensively: anything that is not a stored `1` means unmuted. */
function readBoolean(storage: StorageLike | undefined, key: string): boolean {
  return readLocal(storage, key) === '1'
}
